import { getConfig } from "@jti/fe-common";

import { getUser, isEvoError } from "../../../utils";
import { Rest } from "../../Rest";
import type {
  GetReferralStatsResponseType,
  ReferralApiContract,
  ReferralCodeSubmissionSource,
  ReferralCustomerType,
  ReferralStatusResponseType,
  VerifyResponseType,
} from "../ReferralApi.types";

/** @deprecated Legacy proxy implementation — superseded by EVO. To be deleted after production verification. */
export class ReferralApiLegacy extends Rest implements ReferralApiContract {
  readonly name = "ReferralApi";
  constructor(
    baseUrl: string,
    protected tenantToken: string
  ) {
    super(baseUrl);
    this.setCommonHeader("X-DES-AK-TENANT-TOKEN", this.tenantToken);
  }

  static init(): ReferralApiLegacy {
    const config = getConfig();
    return new ReferralApiLegacy(config.REFERRAL_URL, config.REFERRAL_TOKEN);
  }

  getStatus(): Promise<boolean> {
    return this.request<ReferralStatusResponseType>("/status", {
      method: "GET",
    })
      .then((response) => response.isActive)
      .catch(() => false);
  }

  getCustomer(uuid: string, ciam_token: string, skipCache: boolean = false): Promise<ReferralCustomerType> {
    return this.request<ReferralCustomerType>(
      `/customer/${uuid}`,
      {
        headers: {
          "X-DES-CONSUMER-IAM-TOKEN": ciam_token,
          ...(skipCache && { "X-SKIP-SW-CACHE": "true" }),
        },
        method: "GET",
      },
      true
    ).catch(() => ({
      id: "",
      uuid: "",
      code: "",
      parent_code: "",
      tenant_code: "",
      has_first_purchase: null,
    }));
  }

  verifyCode(code: string, ciam_token: string): Promise<boolean> {
    return this.request<VerifyResponseType>(
      `/verify`,
      {
        body: JSON.stringify({ code: code }),
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
        method: "POST",
      },
      true
    )
      .then((response) => response.is_valid)
      .catch((err) => {
        if (isEvoError(err)) throw err;
        return false;
      });
  }

  getReferralStats(customer_uuid: string, ciam_token: string): Promise<GetReferralStatsResponseType> {
    return this.request<GetReferralStatsResponseType>(
      `/referrer/${customer_uuid}/stats`,
      {
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
        method: "GET",
      },
      true
    )
      .then((response) => ({
        earned_points: response.earned_points,
        referees_count: response.referees_count,
        completed_first_order_count: response.completed_first_order_count,
      }))
      .catch(() => ({
        earned_points: 0,
        referees_count: 0,
        completed_first_order_count: 0,
      }));
  }

  updateReferralCode(uuid: string, referral_code: string, _: string, source?: ReferralCodeSubmissionSource) {
    const { ciam_token } = getUser();
    return this.request<boolean>(
      "/customer",
      {
        method: "PUT",
        body: JSON.stringify({
          uuid,
          referral_code,
          source,
        }),
        headers: { "X-DES-CONSUMER-IAM-TOKEN": ciam_token },
      },
      true
    ).then(() => true);
  }
}
